/**
 * O mapa do cockpit: as áreas, o que cada uma faz e o que ela ainda não faz.
 *
 * **Isto é um só lugar de propósito.** A barra lateral desenha a navegação a partir daqui,
 * e o painel de ajuda de cada tela lê a mesma entrada. Com duas listas, o rótulo do menu e
 * o título da ajuda divergem na primeira vez que alguém renomeia uma tela — e a ajuda passa
 * a explicar um lugar que não existe mais com aquele nome.
 *
 * **`limites` não é rodapé de disclaimer.** É a parte que impede a demonstração de prometer
 * o que o produto não faz. Uma tela que não diz o que lhe falta é lida como completa, e a
 * conversa comercial segue por um caminho que a engenharia não consegue cumprir — que é a
 * forma mais cara possível de descobrir uma lacuna.
 *
 * `perfilMinimo` esconde a área de quem não a alcança. Esconder não é segurança: quem
 * garante é o `exigir_perfil` no servidor, que devolve 403. Aqui é para o operador não
 * clicar num lugar que vai recusá-lo — e para a tela não ensinar que erro é normal.
 *
 * O **regulador** (`OB-13`, ADR-075) não está na hierarquia: ele vê só as áreas marcadas
 * `regulador`, e só lê. No servidor, a rota que não se declara aberta a ele recusa antes de
 * rodar, e cada leitura fica registrada para a concessionária.
 *
 * **Quem decide o que aparece é o servidor** (`T-21`, ADR-085): `GET /navegacao` devolve os
 * endereços das áreas que a pessoa alcança — pela licença, pelo perfil e pelo que o contrato
 * de cada concessão do recorte tem —, e `areasDaNavegacao` só os põe na ordem e com o texto
 * daqui. A área do contrato (a *Autuação*, onde o contrato manda a evasão ao autuador) só
 * existe por ele. `areasVisiveis` é a conta antiga, de licença e perfil, e fica para quando o
 * servidor não responde: sem ela, uma rede instável deixaria o menu vazio.
 */

export type Perfil =
  | "OPERADOR"
  | "SUPERVISOR"
  | "BACKOFFICE"
  | "ADMIN"
  | "APRESENTADOR"
  | "REGULADOR";

export interface Area {
  para: string;
  rotulo: string;
  /** O rótulo da barra inferior do celular (`C-17`): uma palavra, cabe sob um ícone. */
  rotuloCurto: string;
  /** Uma frase. É o subtítulo da tela e a linha de ajuda no menu — a mesma frase. */
  resumo: string;
  /** O que esta tela resolve, em duas ou três linhas, para quem nunca a abriu. */
  ajuda: string;
  /** O que ela **ainda não** faz, e de que história isso depende. */
  limites: string[];
  icone:
    | "imagens"
    | "operacao"
    | "senatran"
    | "prazos"
    | "indicadores"
    | "regulador"
    | "autuacao"
    | "admin";
  perfilMinimo?: Perfil;
  /** O módulo que precisa estar licenciado. Sem ele a área some do menu (`F-05`). */
  modulo?: "analise" | "tollvision";
  /** Some do menu quando este módulo **está** licenciado — porque outra área já a contém.
   *  É o caso do upload: com o TollVision, ele é o botão "Enviar imagens" da Operação. */
  ocultaCom?: "analise" | "tollvision";
  /** O regulador lê esta área (`OB-13`). As outras, ele não vê — e o servidor as recusa. */
  regulador?: true;
  /** A ajuda para quem lê como regulador: a de sempre fala das ações da concessionária. */
  ajudaDoRegulador?: string;
  /** A área só existe onde o contrato de alguma concessão a pede (`T-21`): só o servidor
   *  sabe, e sem a resposta dele ela não aparece. */
  doContrato?: true;
}

export const AREAS: Area[] = [
  {
    para: "/operacao",
    rotulo: "Operação",
    rotuloCurto: "Operação",
    resumo: "Conferir placa, eixos e categoria antes do envio à SENATRAN",
    ajuda:
      "A fila do dia: cada trânsito capturado com as imagens das câmeras e o que cada IA " +
      "leu — placa, eixos, anonimização, isenção, divergência. O operador corrige à mão o " +
      "que a IA errou e aprova; só o aprovado segue para a tela SENATRAN. Na dúvida, " +
      'sinaliza para o supervisor ou escala para o backoffice. "Enviar imagens" põe fotos ' +
      "novas nesta mesma fila.",
    limites: [
      "Os contornos sobre a imagem (caixa da placa, silhueta) esperam a V-05 e a V-06.",
      "A classe do veículo pela IA é a fase 2 do serviço de visão: hoje a categoria é do operador.",
      "O relógio virtual, que avança dias sem esperar, existe só nas organizações de demonstração.",
    ],
    icone: "operacao",
    modulo: "tollvision",
  },
  {
    para: "/senatran",
    rotulo: "SENATRAN",
    rotuloCurto: "SENATRAN",
    resumo: "Envio, retorno e prazos dos trânsitos já validados pela operação",
    ajuda:
      "Só o que a Operação aprovou. Envie um a um ou em lote, acompanhe o retorno da " +
      "SERPRO e os prazos de 24 horas e de 7 dias. Cada cartão do topo é um filtro da " +
      "tabela, e o número dele é o tamanho da lista que abre. A supervisão mantém aqui a " +
      "lista de isentos da concessionária.",
    limites: [
      "Reclassificação de TAG depende da saga da R-07.",
      "A lista de isentos é mantida no cockpit: não é base oficial, e nenhuma base oficial a alimenta.",
      "A integração é o emulador SERPRO (R-05a) até a homologação com o SERPRO de verdade.",
    ],
    icone: "senatran",
    modulo: "tollvision",
    regulador: true,
    ajudaDoRegulador:
      "Os trânsitos que a concessionária validou e mandou à SENATRAN: cada cartão do topo é " +
      "um filtro, e o detalhe traz a passagem, as leituras, a trilha da integração e os " +
      "prazos. A foto de pista não aparece — só a cópia anonimizada, que existe para a " +
      "fraude confirmada.",
  },
  {
    para: "/prazos",
    rotulo: "Prazos e receita em risco",
    rotuloCurto: "Prazos",
    resumo: "O que vence primeiro, quanto vale e quanto se perde se vencer",
    ajuda:
      "As três trilhas que correm contra a arrecadação: o envio à SENATRAN (24 horas da " +
      "passagem), o processamento na SENATRAN (7 dias) e o pagamento (o limite do débito). " +
      "Cada número abre a fila com a mesma contagem, e a fila de urgência traz o que vence " +
      "primeiro, com o crítico em vermelho. A perda é a do regime de cada concessão.",
    limites: [
      "O pagamento que vence não conta como perda: o débito segue em aberto e vai ao autuador.",
      "Os avisos do 8º e do 15º dia pela CNH Digital não são trilha aqui: estão no detalhe de cada trânsito.",
      "O alerta antes de vencer, no WhatsApp, é o boletim de prazos da hora (W-08).",
    ],
    icone: "prazos",
    modulo: "tollvision",
    regulador: true,
    ajudaDoRegulador:
      "As três trilhas que correm contra a arrecadação — o envio (24 horas), o " +
      "processamento (7 dias) e o pagamento —, quanto vence primeiro e quanto se perde se " +
      "vencer, pelo regime de cada concessão. Cada número abre a fila da SENATRAN com a " +
      "mesma contagem.",
  },
  {
    para: "/imagens",
    rotulo: "Processamento de imagens",
    rotuloCurto: "Imagens",
    resumo: "Lotes enviados pelo cliente e o que cada IA leu neles",
    ajuda:
      "Abra um lote, arraste as fotos que o cliente mandou e feche o lote para a fila. " +
      "O worker analisa cada imagem e as leituras aparecem na miniatura. O portão de " +
      "anonimização tem três estados, e o download da cópia anônima só abre no terceiro.",
    limites: [
      "Os contornos sobre a imagem esperam a V-05 e a V-06 devolverem as caixas.",
      "A classe do veículo pela IA é a fase 2 do serviço de visão (V-01).",
    ],
    icone: "imagens",
    modulo: "analise",
    ocultaCom: "tollvision",
  },
  {
    para: "/indicadores",
    rotulo: "Indicadores e Compliance",
    rotuloCurto: "Indicadores",
    resumo:
      "Desempenho, falha e risco — e cada número abre a fila que o produziu",
    ajuda:
      "Seis indicadores sobre o período, a concessionária e a praça escolhidos. Clicar em " +
      "qualquer um abre a tela SENATRAN com exatamente aquele recorte — o número do cartão é " +
      "o tamanho da lista que ele abre, e há teste que confere os seis. A supervisão fecha o " +
      "mês ou o trimestre em Fechamento regulatório: o pacote para o poder concedente, com os " +
      "mesmos números.",
    limites: [
      "Os dois indicadores em reais dependem do valor da tarifa de cada categoria, ainda a confirmar (decisão nº 5).",
      "Reclassificações de TAG dependem da saga da R-07, que ainda não chama a OSA.",
      "A previsão do mês, do trimestre e do ano está no Pergunte ao Cockpit (W-05), não nesta " +
        "tela: a média por dia da semana numa base de dias completos, sem tendência nem feriado.",
    ],
    icone: "indicadores",
    modulo: "tollvision",
    regulador: true,
    ajudaDoRegulador:
      "Os indicadores do período e os índices regulatórios do mês — leitura de placa, " +
      "processamento, pagável em 2 e em 24 horas, disponibilidade —, com a meta do regime. " +
      "Cada número abre a fila que o produziu.",
  },
  {
    para: "/poder-concedente",
    rotulo: "Poder concedente",
    rotuloCurto: "Regulador",
    resumo:
      "O que o contrato manda entregar ao regulador, e o que o cockpit já cobre",
    ajuda:
      "O perfil do poder concedente de cada concessão: as entregas que o contrato pede — " +
      "relatórios, declarações, acessos do regulador, registros —, com o prazo, a cláusula, " +
      "a penalidade e o que o cockpit já faz por cada uma, ao lado do regime de prazos em " +
      "vigor. O calendário mostra quando vence cada entrega, no dia útil do expediente do " +
      "regulador, e a supervisão registra cada uma quando ela sai — a declaração mensal e a " +
      "planilha das transações saem pelo próprio cockpit, com a prévia e o recibo. O administrador escolhe o " +
      "perfil de cada concessão, entre os liberados para a organização, e informa as datas " +
      "do contrato de que o calendário depende. Onde o regulador tem login, a tela mostra " +
      "quem tem acesso e o que ele viu, dia a dia.",
    limites: [
      "O envio vai a um emulador do canal do regulador: nada chega ao regulador até o canal real. O modelo de documento do regulador é a PC-03.",
      "O que o regulador viu aparece só dos últimos 30 dias; a auditoria dos softwares de controle, que o contrato também pede no acesso online, é do auditor (PC-05).",
      "Cada perfil lê um contrato; um contrato novo ganha o perfil dele quando for lido.",
      "O calendário conta só os feriados de lei: o ponto facultativo aparece como aviso e não muda o vencimento.",
    ],
    icone: "regulador",
    perfilMinimo: "SUPERVISOR",
    modulo: "tollvision",
    regulador: true,
    ajudaDoRegulador:
      "O que o contrato de cada concessão manda entregar ao poder concedente, quando vence " +
      "cada entrega no expediente do regulador e o que já foi entregue ou enviado pelo " +
      "cockpit, com o protocolo e o recibo.",
  },
  {
    para: "/autuacao",
    rotulo: "Autuação",
    rotuloCurto: "Autuação",
    resumo: "A evasão que espera o envio ao autuador, e até quando ela pode ir",
    ajuda:
      "Só onde o contrato manda a concessionária enviar a evasão ao autuador — em MS, o " +
      "relatório semanal à AGESUL, que não leva registro com mais de 7 dias da evasão. Cada " +
      "evasão que o prazo do usuário deixou sem pagamento entra aqui com o fim do prazo do " +
      "autuador: o que está no prazo, perto do fim, no último dia e o que já passou. Quem " +
      "pagou nunca aparece, nem depois do prazo; o isento e o cancelado também não.",
    limites: [
      "O arquivo semanal, o canal do autuador e o atestado dele são a OB-17: nada sai pelo cockpit ainda.",
      "A evasão que passou dos 7 dias aparece como fora do prazo; contá-la como perda, com o motivo, é da OB-17.",
      "Outro autuador, com outro relatório, entra quando o contrato da concessão o pedir (OB-03).",
    ],
    icone: "autuacao",
    perfilMinimo: "SUPERVISOR",
    modulo: "tollvision",
    doContrato: true,
  },
  {
    para: "/administracao",
    rotulo: "Administração",
    rotuloCurto: "Admin",
    resumo: "Usuários, chaves de pórtico, licenças, configuração e saúde",
    ajuda:
      "A administração desta organização — e só dela: quem tem acesso e com que perfil, as " +
      "chaves dos pórticos com o último uso de cada uma, o que foi licenciado e consumido " +
      "de IA, os parâmetros por concessão e o panorama de volume e saúde.",
    limites: [
      "A visão entre clientes — suporte, faturamento, infraestrutura — é a T-09, e exige papel próprio e trilha de quem olhou o quê.",
      "Provisionar uma organização nova é feito pela IA Solutions, fora da aplicação (T-06).",
      "A retenção de imagens é configurável e ainda não apaga nada: o expurgo pelo prazo de guarda é a OB-09.",
    ],
    icone: "admin",
    perfilMinimo: "ADMIN",
  },
];

/** Hierarquia dos perfis. `ADMIN` alcança tudo, como no `exigir_perfil` do servidor. */
const ALCANCE: Record<Perfil, number> = {
  OPERADOR: 1,
  SUPERVISOR: 2,
  BACKOFFICE: 2,
  // Quem apresenta vê o que o supervisor vê (`O-06`): mostra o cockpit inteiro, menos a
  // Administração.
  APRESENTADOR: 2,
  ADMIN: 3,
  // Fora da hierarquia: o regulador vê as áreas marcadas `regulador`, e mais nada.
  REGULADOR: 0,
};

/** O poder concedente lendo o cockpit (`OB-13`): só leitura, nas áreas abertas a ele. */
export function eRegulador(perfil: string | null | undefined): boolean {
  return perfil === "REGULADOR";
}

/**
 * Quem mexe nos controles da demonstração — o cenário da SENATRAN, "Simular prazo vencendo",
 * o relógio, "Limpar uploads" (`O-06`, ADR-062): o apresentador e o administrador, e só em
 * organização de demonstração. O servidor confere o mesmo, e devolve 403 a quem não pode.
 */
export function controlaADemonstracao(
  quem:
    { perfil?: string | null; modo_demonstracao?: boolean } | null | undefined,
): boolean {
  return Boolean(
    quem?.modo_demonstracao &&
    (quem.perfil === "APRESENTADOR" || quem.perfil === "ADMIN"),
  );
}

/** Quem decide como supervisor: o supervisor, o administrador e o apresentador (`O-06`). */
export function supervisiona(perfil: string | null | undefined): boolean {
  return (
    perfil === "SUPERVISOR" || perfil === "ADMIN" || perfil === "APRESENTADOR"
  );
}

/** Quem recebe as notificações internas na tela (`R-08`): a supervisão, o backoffice, o
 *  apresentador e o administrador. O operador, não; o regulador, também não. */
export function recebeNotificacoes(perfil: string | null | undefined): boolean {
  return supervisiona(perfil) || perfil === "BACKOFFICE";
}

/**
 * As áreas que o servidor diz que a pessoa alcança (`GET /navegacao`), na ordem e com o texto
 * daqui. Endereço que o servidor manda e esta lista não conhece fica de fora: uma área nova no
 * servidor sem o texto aqui não aparece pela metade.
 */
export function areasDaNavegacao(codigos: readonly string[]): Area[] {
  return AREAS.filter((area) => codigos.includes(area.para));
}

/** A conta de licença e perfil, para quando o servidor não respondeu o menu: as áreas do
 *  contrato ficam de fora, porque só ele sabe onde elas valem. */
export function areasVisiveis(
  perfil: string | null | undefined,
  licenciados: readonly string[],
): Area[] {
  return AREAS.filter((area) => {
    if (area.doContrato) return false;
    // Módulo não licenciado some do menu em vez de aparecer e dar 402 no clique: é o que
    // `GET /modulos` existe para permitir, e é a diferença entre "você não contratou" e
    // "o sistema deu erro".
    if (area.modulo && !licenciados.includes(area.modulo)) return false;
    if (area.ocultaCom && licenciados.includes(area.ocultaCom)) return false;
    if (eRegulador(perfil)) return area.regulador === true;
    if (!area.perfilMinimo) return true;
    const meu = perfil && perfil in ALCANCE ? ALCANCE[perfil as Perfil] : 0;
    return meu >= ALCANCE[area.perfilMinimo];
  });
}

export function areaDe(caminho: string): Area | undefined {
  return AREAS.find((a) => caminho.startsWith(a.para));
}

/** O *Pergunte ao Cockpit* cabe nesta tela? Em **toda** área do cockpit (03/11): a Gisele, na
 *  revisão da homologação, precisava abrir a Administração para achar o botão — e a pessoa
 *  pode querer perguntar a qualquer momento, inclusive na mesa da Operação. Era só nas áreas
 *  de gestão (06/10), pelo receio de o botão distrair o operador; ele é um cartão da lateral
 *  (e um botão do topo, no celular), nunca cobre a conferência. Nunca para o regulador, que
 *  não conversa com o agente da concessionária (`OB-13`). */
export function agenteNaTela(caminho: string, perfil?: string | null): boolean {
  if (eRegulador(perfil)) return false;
  return areaDe(caminho) != null;
}
